import { Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import { FC, useEffect } from 'react';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { getFiltersFromNavigateParams, getOrderByFromNavigateParams } from 'utils/FilterParams';
import {
	getEudrStatus,
	getEudrStatusIsLoading,
	getUploadedFiles,
	getUploadedFilesIsLoading,
	getUploadFileIsLoading,
} from 'redux/reducers/eudr/selectors';
import {
	getEudrStatusRequest,
	getUploadedFilesRequest,
	uploadFileRequest,
	downloadFileRequest,
} from 'redux/reducers/eudr/reducer';
import { TEudrUploadedFile } from 'redux/reducers/eudr/types';
import { VisuallyHiddenInput } from 'ui/VisuallyHiddenInput';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { KeyboardBackspace } from '@mui/icons-material';
import { PATHS } from 'routes/constants';
import { eudrFilesTableStructure } from './tableStructure/eudrFilesTableStructure';

export const EudrFiles: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const eudrStatus = useSelector(getEudrStatus);
	const eudrStatusIsLoad = useSelector(getEudrStatusIsLoading);
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const items = useSelector(getUploadedFiles);
	const itemsIsLoading = useSelector(getUploadedFilesIsLoading);
	const uploadFileLoading = useSelector(getUploadFileIsLoading);

	const tableStructure: TTableStructure<keyof TEudrUploadedFile>[] = eudrFilesTableStructure;

	useEffect(
		() => {
			dispatch(getEudrStatusRequest());
		},
		// eslint-disable-next-line
		[],
	);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(tableStructure, navigateParams);

	useEffect(() => {
		dispatch(
			getUploadedFilesRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams),
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			const formData = new FormData();
			formData.append('file', file);
			formData.append('comments', ''); // You can add a dialog to get comments if needed
			dispatch(uploadFileRequest(formData));
			// Refresh list after upload (or handle it in saga)
			setTimeout(() => {
				dispatch(
					getUploadedFilesRequest({
						pageNumber: page ? +page : 1,
						pageSize: PAGE_SIZE,
						searchString: String(navigateParams?.search ?? ''),
						filters: navigateParamsWithoutPage,
						orderBy: [],
					}),
				);
			}, 1000);
		}
	};

	const handleDownloadFile = (id: number) => {
		dispatch(downloadFileRequest(id));
	};

	const handleEudrActions = (id: number, typeOfDialog: string) => {
		if (typeOfDialog === 'Download') {
			handleDownloadFile(id);
		}
	};

	const handleBack = () => {
		navigate(PATHS.EUDR_SERVICE_PAGE);
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			{eudrStatusIsLoad && <Skeleton variant="rectangular" height={530} />}
			{/* Add banner logic if needed */}
			{eudrStatus?.enabled && (
				<EasTableWithFilter<TEudrUploadedFile>
					tableStructure={eudrFilesTableStructure}
					items={items}
					itemsIsLoading={itemsIsLoading}
					handleAction={handleEudrActions}
					highlightedRow={null}
					showButtons={items?.data?.map(() => {
						return {
							Download: true,
						};
					})}
					minWidth={1000}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<IconButton color="primary" size="small" onClick={handleBack}>
								<KeyboardBackspace />
							</IconButton>
							<Typography
								variant="h1"
								color="primary"
								sx={{
									fontSize: '1.1rem',
									fontWeight: 'bold',
									textAlign: 'center',
									ml: 1,
								}}
							>
								Uploaded EUDR Files
							</Typography>
						</Box>
						<Button
							component="label"
							variant="contained"
							startIcon={<CloudUploadIcon />}
							disabled={uploadFileLoading}
							size="small"
						>
							{uploadFileLoading ? 'Uploading...' : 'Upload File'}
							<VisuallyHiddenInput type="file" onChange={handleUploadFile} accept=".csv" />
						</Button>
					</Box>
				</EasTableWithFilter>
			)}
		</Box>
	);
};
